<template>
  <div class="videocp-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:film" width="24" height="24" />
        视频库管理
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.video_name" placeholder="请输入视频库名称" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增视频库</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="galleryList" stripe v-loading="loading" class="videocp-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column prop="video_name" label="视频库名称" width="200" />
          <el-table-column label="素材总数" width="120">
            <template #default="scope">
              <el-tag>{{ scope.row.total_count || 0 }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="未使用数" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.unused_count > 0 ? 'warning' : 'success'">
                {{ scope.row.unused_count || 0 }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="notes" label="备注" min-width="150" show-overflow-tooltip />
          <el-table-column prop="create_time" label="创建时间" width="180" />
          <el-table-column prop="update_time" label="更新时间" width="180" />

          <el-table-column label="操作" width="400" fixed="right">
            <template #default="scope">
              <button class="action-btn info-btn" @click="openMaterialDialog(scope.row)">
                <Icon icon="heroicons:film" width="14" height="14" />
                查看素材
              </button>
              <button class="action-btn warning-btn" :disabled="refreshingId === scope.row.id" @click="refreshByGallery(scope.row)">
                <Icon icon="heroicons:arrow-path" width="14" height="14" />
                {{ refreshingId === scope.row.id ? '刷新中...' : '刷新' }}
              </button>
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchGalleryList" @current-change="fetchGalleryList" background />
      </div>
    </div>

    <!-- 新增/编辑视频库对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <el-form ref="galleryForm" :model="galleryForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="视频库名称" prop="video_name">
          <el-input v-model="galleryForm.video_name" placeholder="请输入视频库名称" size="large" />
        </el-form-item>
        <el-form-item label="备注信息">
          <el-input v-model="galleryForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitGalleryForm">确认</button>
      </template>
    </el-dialog>

    <!-- 视频成品管理对话框 -->
    <el-dialog v-model="materialDialogVisible" :title="materialDialogTitle" :width="materialDialogWidth" top="5vh"
      :close-on-click-modal="false" :lock-scroll="false" :append-to-body="true"
      class="custom-dialog material-view-dialog">
      <!-- 视频成品搜索 -->
      <div class="material-search-bar">
        <div style="display:flex; align-items:center; gap:12px; flex-wrap: wrap;">
          <el-input v-model="materialSearchForm.video_name" placeholder="请输入视频名称" clearable size="large"
            style="width: 300px" @keyup.enter="handleMaterialSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleMaterialSearch">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            搜索视频
          </el-button>

          <el-button type="warning" size="large" :loading="dialogRefreshing" @click="refreshCurrentGallery">
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            刷新拉取
          </el-button>
        </div>

        <div style="display:flex; align-items:center; gap:12px;">
          <el-button type="success" size="large" @click="openAddMaterialDialog">
            <Icon icon="heroicons:plus" width="18" height="18" />
            新增视频
          </el-button>

          <el-button type="warning" size="large" @click="triggerBatchUpload">
            <Icon icon="heroicons:arrow-up-tray" width="18" height="18" />
            批量上传
          </el-button>
          <input type="file" ref="batchUploadInput" style="display: none" multiple accept="video/*"
            @change="handleBatchUpload" />
        </div>
      </div>

      <!-- 视频成品列表 -->
      <div class="material-list">
        <div v-for="item in materialList" :key="item.id" class="material-item">
          <video controls :src="item.video_url" class="material-video" :title="item.video_name" preload="metadata"></video>
          <div class="material-info">
            <div class="material-name">{{ item.video_name || '未命名视频' }}</div>
            <div class="material-actions">
              <el-tag size="small">使用次数: {{ item.use_num }}</el-tag>
              <el-button type="primary" size="small" @click="openEditMaterialDialog(item)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                编辑
              </el-button>
              <el-button type="danger" size="small" @click="handleMaterialDelete(item.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                删除
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 素材分页 -->
      <div class="pagination-container" v-show="materialPagination.total > 0">
        <el-pagination v-model:current-page="materialPagination.page" v-model:page-size="materialPagination.pageSize"
          :total="materialPagination.total" :page-sizes="[6, 12, 24, 36]"
          layout="total, sizes, prev, pager, next, jumper" @size-change="fetchMaterialList"
          @current-change="fetchMaterialList" />
      </div>

      <!-- 视频成品为空提示 -->
      <el-empty v-if="materialList.length === 0" description="暂无视频成品" />
    </el-dialog>

    <!-- 新增/编辑视频成品对话框 -->
    <el-dialog v-model="materialFormDialogVisible" :title="materialFormDialogTitle" :width="dialogWidth"
      :close-on-click-modal="false" :append-to-body="true" class="child-dialog" :top="dialogTop">
      <el-form ref="materialForm" :model="materialForm" :label-width="formLabelWidth">
        <el-form-item label="视频名称">
          <el-input v-model="materialForm.video_name" placeholder="请输入视频名称" size="large" />
        </el-form-item>

        <el-form-item label="上传视频" prop="video_url">
          <el-upload class="upload-demo" :auto-upload="false" :show-file-list="false" :on-change="handleImageChange"
            accept="video/*">
            <el-button type="primary">选择视频</el-button>
            <template #tip>
              <div class="el-upload__tip">请上传视频文件</div>
            </template>
          </el-upload>
          <div v-if="materialForm.video_url" class="upload-preview">
            已上传视频: {{ materialForm.video_url }}
          </div>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="materialForm.notes" placeholder="请输入备注" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="materialFormDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitMaterialForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 刷新结果弹窗 -->
    <el-dialog v-model="refreshResultVisible" title="刷新结果" width="640px"
      :close-on-click-modal="false" :append-to-body="true" class="child-dialog">
      <div v-if="refreshResult">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="处理总数">{{ refreshResult.processed || 0 }}</el-descriptions-item>
          <el-descriptions-item label="成功">{{ refreshResult.success || 0 }}</el-descriptions-item>
          <el-descriptions-item label="待完成">{{ refreshResult.pending || 0 }}</el-descriptions-item>
          <el-descriptions-item label="失败/重试">{{ refreshResult.failed || 0 }}</el-descriptions-item>
          <el-descriptions-item label="余额不足">{{ refreshResult.insufficient || 0 }}</el-descriptions-item>
        </el-descriptions>

        <div style="margin-top: 12px; max-height: 320px; overflow: auto;">
          <el-table :data="refreshResult.details || []" size="small" border>
            <el-table-column prop="job_id" label="Job ID" min-width="200" show-overflow-tooltip />
            <el-table-column prop="status" label="状态" width="100" />
            <el-table-column prop="media_url" label="视频URL" min-width="220" show-overflow-tooltip />
            <el-table-column prop="message" label="说明" min-width="200" show-overflow-tooltip />
            <el-table-column prop="retry_count" label="重试次数" width="100" />
          </el-table>
        </div>
      </div>

      <template #footer>
        <el-button type="primary" @click="refreshResultVisible = false">知道了</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'MaterialVideoCp',
  components: { Icon },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增视频库',
      isEditMode: false,
      searchForm: { video_name: '' },
      galleryForm: { id: null, video_name: '', notes: '' },
      formRules: { video_name: [{ required: true, message: '请输入视频库名称', trigger: 'blur' }] },
      galleryList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },

      materialDialogVisible: false,
      materialFormDialogVisible: false,
      materialDialogTitle: '视频成品管理',
      materialFormDialogTitle: '新增视频成品',
      materialSearchForm: { video_name: '' },
      materialForm: { id: null, video_name: '', video_url: '', use_num: 0, notes: '', video_id: null, store_id: null },
      materialList: [],
      materialPagination: { page: 1, pageSize: 6, total: 0 },
      currentGallery: {},
      materialLoading: false,
      isMaterialEditMode: false,

      refreshingId: null,
      dialogRefreshing: false,
      refreshResultVisible: false,
      refreshResult: null,

      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    previewList() { return this.materialList.map(item => item.video_url) },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '600px'
    },
    materialDialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '1200px'
    },
    dialogTop() {
      if (this.screenWidth < 576) return '5vh'
      return '10vh'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '120px'
    }
  },
  mounted() {
    this.fetchGalleryList();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.screenWidth = window.innerWidth },

    async callRefreshApi(videoCpId) {
      const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_management.php`, {
        action: 'refresh',
        store_id: this.userInfo.id,
        id: videoCpId
      });
      return data;
    },

    async refreshByGallery(row) {
      try {
        this.refreshingId = row.id;
        const data = await this.callRefreshApi(row.id);
        if (data?.success) {
          this.refreshResult = data.data || null;
          this.refreshResultVisible = true;
          if (this.materialDialogVisible && this.currentGallery?.id === row.id) {
            await this.fetchMaterialList();
          }
          await this.fetchGalleryStats();
          ElMessage.success('刷新完成');
        } else {
          ElMessage.error(data?.error || '刷新失败');
        }
      } catch (e) {
        ElMessage.error('刷新失败');
        console.error(e);
      } finally {
        this.refreshingId = null;
      }
    },

    async refreshCurrentGallery() {
      if (!this.currentGallery?.id) return;
      try {
        this.dialogRefreshing = true;
        const data = await this.callRefreshApi(this.currentGallery.id);
        if (data?.success) {
          this.refreshResult = data.data || null;
          this.refreshResultVisible = true;
          await this.fetchMaterialList();
          await this.fetchGalleryStats();
          ElMessage.success('刷新完成');
        } else {
          ElMessage.error(data?.error || '刷新失败');
        }
      } catch (e) {
        ElMessage.error('刷新失败');
        console.error(e);
      } finally {
        this.dialogRefreshing = false;
      }
    },

    async fetchGalleryList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.video_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };
        if (this.searchForm.video_name) params.video_name = this.searchForm.video_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_management.php`, params);
        if (data.data) {
          this.galleryList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
          await this.fetchGalleryStats();
        }
      } catch (error) {
        ElMessage.error('获取视频库列表失败');
        console.error('获取视频库列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchGalleryStats() {
      try {
        const statsPromises = this.galleryList.map(gallery =>
          axios.post(`${config.BASE_URL}/shop/api/video_cp_management.php`, {
            action: 'stats',
            store_id: this.userInfo.id,
            video_id: gallery.id
          }).catch(() => null)
        );
        const statsResults = await Promise.all(statsPromises);
        this.galleryList = this.galleryList.map((gallery, index) => {
          const stats = statsResults[index]?.data?.data || {};
          return { ...gallery, total_count: stats.total_count || 0, unused_count: stats.unused_count || 0 };
        });
      } catch (error) {
        console.error('获取视频库统计数据错误:', error);
      }
    },

    handleSearch() { this.pagination.page = 1; this.fetchGalleryList(); },

    openAddDialog() {
      this.dialogTitle = '新增视频库'; this.isEditMode = false;
      this.galleryForm = { id: null, video_name: '', notes: '' };
      this.dialogVisible = true;
    },

    openEditDialog(row) {
      this.dialogTitle = '编辑视频库'; this.isEditMode = true;
      this.galleryForm = { id: row.id, video_name: row.video_name, notes: row.notes };
      this.dialogVisible = true;
    },

    submitGalleryForm() {
      this.$refs.galleryForm.validate(async (valid) => {
        if (!valid) return;
        try {
          const action = this.isEditMode ? 'update' : 'add';
          const payload = { ...this.galleryForm, store_id: this.userInfo.id };
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_management.php`, { action, ...payload });
          if (data.success) {
            ElMessage.success(this.isEditMode ? '更新成功' : '添加成功');
            this.dialogVisible = false;
            this.fetchGalleryList();
          } else {
            ElMessage.error(data.error || '操作失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '操作失败';
          ElMessage.error(`操作失败: ${errorMsg}`);
          console.error('视频库表单提交错误:', error);
        }
      });
    },

    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该视频库吗？', '警告', {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_management.php`, {
          action: 'delete', id, store_id: this.userInfo.id
        });
        if (data.success) {
          ElMessage.success('删除成功');
          this.fetchGalleryList();
        } else {
          ElMessage.error(data.error || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          ElMessage.error(`删除失败: ${errorMsg}`);
          console.error('视频库删除错误:', error);
        }
      }
    },

    openMaterialDialog(gallery) {
      this.currentGallery = gallery;
      this.materialDialogTitle = `${gallery.video_name} - 视频成品管理`;
      this.materialDialogVisible = true;
      this.materialSearchForm.video_name = '';
      this.materialPagination.page = 1;
      this.fetchMaterialList();
      this.fetchGalleryStats();
    },

    async fetchMaterialList() {
      this.materialLoading = true;
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          video_id: this.currentGallery.id,
          page: this.materialPagination.page,
          page_size: this.materialPagination.pageSize
        };
        if (this.materialSearchForm.video_name) {
          params.action = 'search';
          params.video_name = this.materialSearchForm.video_name;
        }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_materials.php`, params);
        if (data && data.data) {
          this.materialList = data.data;
          this.materialPagination = {
            page: data.pagination?.page || this.materialPagination.page,
            pageSize: data.pagination?.page_size || this.materialPagination.pageSize,
            total: data.pagination?.total || data.data.length
          };
        }
      } catch (error) {
        ElMessage.error('获取视频成品失败');
        console.error('获取视频成品错误:', error);
      } finally {
        this.materialLoading = false;
      }
    },

    handleMaterialSearch() {
      this.materialPagination.page = 1;
      this.fetchMaterialList();
      this.fetchGalleryStats();
    },

    openAddMaterialDialog() {
      this.materialFormDialogTitle = '新增视频成品';
      this.isMaterialEditMode = false;
      this.materialForm = {
        id: null, video_name: '', use_num: 0, video_url: '', notes: '',
        video_id: this.currentGallery.id, store_id: this.userInfo.id
      };
      this.materialFormDialogVisible = true;
    },

    openEditMaterialDialog(item) {
      this.materialFormDialogTitle = '编辑视频成品';
      this.isMaterialEditMode = true;
      this.materialForm = {
        id: item.id, video_name: item.video_name, video_url: item.video_url,
        notes: item.notes, video_id: this.currentGallery.id, store_id: this.userInfo.id
      };
      this.materialFormDialogVisible = true;
    },

    async submitMaterialForm() {
      try {
        const action = this.isMaterialEditMode ? 'update' : 'add';
        const formData = { ...this.materialForm };
        delete formData.create_time;
        delete formData.update_time;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_materials.php`, {
          action, ...formData
        });
        if (data && data.success) {
          ElMessage.success(this.isMaterialEditMode ? '视频更新成功' : '视频添加成功');
          this.materialFormDialogVisible = false;
          this.fetchMaterialList();
          this.fetchGalleryStats();
        } else {
          ElMessage.error(data.error || '操作失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '操作失败';
        ElMessage.error(`操作失败: ${errorMsg}`);
        console.error('视频成品表单提交错误:', error);
      }
    },

    handleMaterialDelete(id) {
      ElMessageBox.confirm('确定要删除该视频成品吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_materials.php`, {
            action: 'delete', id, store_id: this.userInfo.id
          });
          if (data && data.success) {
            ElMessage.success('删除成功');
            this.fetchMaterialList();
            this.fetchGalleryStats();
          } else {
            ElMessage.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          ElMessage.error(`删除失败: ${errorMsg}`);
          console.error('视频成品删除错误:', error);
        }
      }).catch(() => { });
    },

    triggerBatchUpload() { this.$refs.batchUploadInput.click(); },

    async handleBatchUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const loadingInstance = ElMessage({
        message: `正在上传 ${files.length} 个视频...`,
        type: 'info',
        duration: 0
      });

      try {
        const uploadPromises = Array.from(files).map(file => {
          return this.uploadImage(file).then(url => ({
            video_id: this.currentGallery.id,
            store_id: this.userInfo.id,
            video_url: url,
            video_name: file.name.replace(/\.[^/.]+$/, ""),
            use_num: 0,
            notes: ''
          })).catch(error => ({
            error: true, fileName: file.name, message: error.message || '上传失败'
          }));
        });

        const results = await Promise.all(uploadPromises);
        const successful = results.filter(x => !x.error);
        const failed = results.filter(x => x.error);

        if (successful.length === 0 && failed.length > 0) {
          const msg = failed.map(f => `${f.fileName}: ${f.message}`).join('\n');
          throw new Error(`所有视频上传失败:\n${msg}`);
        }
        if (failed.length > 0) {
          const msg = failed.map(f => `${f.fileName}: ${f.message}`).join('\n');
          ElMessage.warning(`${failed.length} 个视频上传失败:\n${msg}`);
        }

        if (successful.length > 0) {
          await this.saveBatchMaterials(successful);
          ElMessage.success(`成功上传 ${successful.length}/${files.length} 个视频`);
          this.fetchMaterialList();
          this.fetchGalleryStats();
        }
      } catch (error) {
        ElMessage.error(`批量上传失败: ${error.message}`);
        console.error('批量上传错误:', error);
      } finally {
        loadingInstance.close();
        event.target.value = '';
      }
    },

    async saveBatchMaterials(materials) {
      const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_cp_materials.php`, {
        action: 'add',
        batch: materials,
        store_id: this.userInfo.id
      });
      if (!data || !data.success) throw new Error(data.error || '批量保存失败');
    },

    async handleImageChange(file) {
      try {
        ElMessage.info('视频上传中，请稍候...');
        const url = await this.uploadImage(file.raw);
        this.materialForm.video_url = url;
        ElMessage.success('视频上传成功');
      } catch (error) {
        ElMessage.error(`视频上传失败: ${error.message}`);
        console.error('上传错误:', error);
      }
    },

    async uploadImage(file) {
      try {
        const allowedTypes = [
          'video/mp4', 'video/quicktime', 'video/x-flv', 'video/x-f4v', 'video/webm',
          'video/x-matroska', 'video/vnd.rn-realvideo', 'video/mp4v-es', 'video/3gpp',
          'video/3gpp2', 'video/x-ms-wmv', 'video/x-msvideo', 'video/x-ms-asf', 'video/mpeg', 'video/MP2T'
        ];
        const maxSize = 200 * 1024 * 1024;

        if (allowedTypes.indexOf(file.type) === -1) {
          throw new Error(`不支持的文件类型: ${file.type}`);
        }
        if (file.size > maxSize) {
          throw new Error(`文件大小超过限制 (200MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        }

        const signature = await getUploadSignature(file.type.split('/')[0]);
        const res = await uploadToOSS(file, signature);
        return res.url;
      } catch (error) {
        console.error('视频上传失败:', error);
        throw error;
      }
    },

    handleUploadSuccess(response) {
      if (response.success) {
        ElMessage.success(`成功上传 ${response.data.length} 个视频`);
        this.fetchMaterialList();
        this.fetchGalleryStats();
      } else {
        ElMessage.error(response.error || '上传失败');
      }
    },

    handleUploadError(error) {
      ElMessage.error(`上传失败: ${error.message}`);
    }
  }
}
</script>

<style scoped lang="scss">
.videocp-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .search-left .search-btn {
      width: 100%;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;

  &.info-btn {
    background: rgba(59, 130, 246, 0.15);
    border: 1px solid rgba(59, 130, 246, 0.4);
    color: #60a5fa;

    &:hover {
      background: rgba(59, 130, 246, 0.25);
    }
  }

  &.warning-btn {
    background: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.4);
    color: #fbbf24;

    &:hover {
      background: rgba(245, 158, 11, 0.25);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }

  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

/* 素材搜索栏 */
.material-search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(18, 14, 33, 0.4);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(157, 78, 221, 0.2);
}

/* 素材列表 */
.material-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.material-item {
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s;
  background: rgba(18, 14, 33, 0.4);

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
}

.material-video {
  width: 100%;
  height: 200px;
  display: block;
  background-color: #000;
  object-fit: contain;
  cursor: pointer;
}

.material-info {
  padding: 12px;

  .material-name {
    font-weight: 500;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #e0e0e0;
  }

  .material-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.upload-demo {
  width: 100%;
}

.upload-preview {
  margin-top: 10px;
  padding: 8px;
  background: rgba(26, 21, 53, 0.6);
  border-radius: 4px;
  word-break: break-all;
  font-size: 14px;
  color: #a0a0c0;
  border: 1px solid rgba(157, 78, 221, 0.2);
}

:deep(.el-upload__tip) {
  color: #7a7a9a !important;
}

:deep(.el-empty__description) {
  color: #a0a0c0 !important;
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.custom-dialog) .el-dialog__body {
  background: rgba(18, 14, 33, 0.98) !important;
}

.child-dialog {
  z-index: 3000 !important;
}

body>.el-overlay:has(.child-dialog) {
  z-index: 3000 !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
  border: 1px solid rgba(157, 78, 221, 0.4) !important;
  border-radius: 20px;
  background: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog__header {
  border-bottom: 1px solid rgba(157, 78, 221, 0.2);
  padding: 20px 24px;
  background: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog__title {
  color: #ffffff !important;
  font-weight: 600;
}

body>.el-overlay:has(.child-dialog) .el-dialog__body {
  padding: 24px;
  color: #e0e0e0;
  background: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog__footer {
  border-top: 1px solid rgba(157, 78, 221, 0.2);
  padding: 16px 24px;
  background: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-input__wrapper {
  background: rgba(26, 21, 53, 0.8) !important;
  border: 1px solid rgba(157, 78, 221, 0.3) !important;
  box-shadow: none !important;
  border-radius: 10px;
}

body>.el-overlay:has(.child-dialog) .el-input__wrapper:hover {
  border-color: rgba(157, 78, 221, 0.6) !important;
}

body>.el-overlay:has(.child-dialog) .el-input__wrapper.is-focus {
  border-color: #9d4edd !important;
  box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2) !important;
}

body>.el-overlay:has(.child-dialog) .el-input__inner {
  color: #fff !important;
  background: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-textarea__inner {
  background: rgba(26, 21, 53, 0.8) !important;
  border: 1px solid rgba(157, 78, 221, 0.3) !important;
  color: #fff !important;
  border-radius: 10px;
}

body>.el-overlay:has(.child-dialog) .el-upload-dragger {
  background: rgba(26, 21, 53, 0.6) !important;
  border-color: rgba(157, 78, 221, 0.4) !important;
}
</style>